import { createPortal } from 'react-dom';
import { useMemo } from 'react';

import { SelectOption, Account } from '~/shared/utils/types';
import FilterButton from '~/shared/components/filters/FilterButton';
import SidePanel from '~/shared/components/SidePanel';

import FilterPanelHeader from '~/shared/components/filters/FilterPanelHeader';
import {
  useVolumeFilterState,
  useVolumeCapacityBounds,
  panelStateHasActiveFilters,
  VolumeFilterPanelContent,
  type AppliedVolumeFilters,
} from '~/shared/components/filters/volumeFilters';

export type GroupReportselectionOption = {
  label: string;
  options: SelectOption[];
};

interface ReportsFiltersSidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResetFilters: () => void;
  /** Everything already applied via the URL — the panel seeds pending state from this. */
  applied: AppliedVolumeFilters;
  activeFilterCount: number;
  setSidePanelOpen: (open: boolean) => void;
  isSidePanelOpen: boolean;
  isOrgView?: boolean;
  accounts?: Account[];
  activeOrgId?: string;
}

export default function ReportsFiltersSidePanel({
  open,
  onOpenChange,
  onResetFilters,
  applied,
  activeFilterCount,
  setSidePanelOpen,
  isSidePanelOpen,
  isOrgView = false,
  accounts = [],
  activeOrgId,
}: ReportsFiltersSidePanelProps) {
  const panelId = 'reports-filters-panel';

  const filterableAccounts = useMemo(() => {
    if (!activeOrgId) {
      return accounts;
    }
    return accounts.filter((account) => account.accountId !== activeOrgId);
  }, [accounts, activeOrgId]);

  // Reports' own table holds opt-log actions, not volumes, so there is nothing local to measure —
  // but the ceiling is a property of the tenant, not of the table, so it comes from the same
  // query All Volumes uses and the two panels agree.
  //
  // Pre-fetched unconditionally so the real max is ready when the panel opens.
  // `staleTime: Infinity` keeps it to one request per session.
  const capacityBounds = useVolumeCapacityBounds();

  const filterState = useVolumeFilterState({ open, capacityBounds, ...applied });

  const hasActiveFilters = panelStateHasActiveFilters(filterState.pending);

  const handleClose = () => {
    filterState.applyPendingFilters();
  };

  const handleResetFilters = () => {
    filterState.resetPendingFilters();
    onResetFilters();
    onOpenChange(false);
  };

  const panel = (
    <SidePanel
      id={panelId}
      open={open}
      onOpenChange={onOpenChange}
      width="w-[445px]"
      zIndexClass="z-[2000]"
      ariaLabel="Reports filters"
      onClose={handleClose}
    >
      <div className="flex flex-col h-full">
        <FilterPanelHeader
          onClearAll={handleResetFilters}
          hasActiveFilters={hasActiveFilters}
          onClose={() => {
            handleClose();
            onOpenChange(false);
          }}
        />

        <VolumeFilterPanelContent
          panelOpen={open}
          pending={filterState.pending}
          setField={filterState.setField}
          setCapacityMin={filterState.setCapacityMin}
          setCapacityMax={filterState.setCapacityMax}
          clearCapacity={filterState.clearCapacity}
          isOrgView={isOrgView}
          accounts={filterableAccounts}
        />
      </div>
    </SidePanel>
  );

  return (
    <>
      <div className="flex gap-2 items-center justify-end w-full max-w-[713px]">
        <FilterButton
          activeFilterCount={activeFilterCount}
          onOpen={() => setSidePanelOpen(!isSidePanelOpen)}
          onClearAll={handleResetFilters}
          size="md-responsive"
          variant="third"
          aria-expanded={isSidePanelOpen}
          aria-controls={panelId}
        />
      </div>
      {typeof document !== 'undefined' ? createPortal(panel, document.body) : null}
    </>
  );
}
